import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { Slider } from '@/components/ui/slider';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { Building2, Shield, BarChart3, Save, Loader2, Scale, Sparkles, Crown } from 'lucide-react';
import { BuildBuyProtocolTab } from '@/components/admin/BuildBuyProtocolTab';
import { toast } from '@/hooks/use-toast';
import { LIDER_IA_COMPONENTS, DIMENSION_COLORS, DIMENSION_NAMES } from '@/data/liderIAEvaluationData';
import { useAuth } from '@/contexts/AuthContext';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useOrgPlan } from '@/hooks/useOrgPlan';
import { useEnterpriseLicenseStats } from '@/hooks/useEnterpriseLicenseStats';
import { RequestEnterpriseLicensesDialog } from '@/components/admin/RequestEnterpriseLicensesDialog';
import { formatContractDate } from '@/lib/orgContract';

const DEFAULT_IMPACT_LEVELS = [
  { id: 1, level: 'Insignificante', description: 'Sin impacto significativo en la operación', value: 1 },
  { id: 2, level: 'Menor', description: 'Impacto limitado, manejable con recursos existentes', value: 2 },
  { id: 3, level: 'Moderado', description: 'Afecta procesos clave, requiere intervención', value: 3 },
  { id: 4, level: 'Mayor', description: 'Impacto significativo en objetivos estratégicos', value: 4 },
  { id: 5, level: 'Catastrófico', description: 'Amenaza la continuidad del negocio', value: 5 },
];

export function EnterpriseSettingsPage() {
  const { profile } = useAuth();
  const orgId = profile?.organization_id;
  const queryClient = useQueryClient();
  const { isFreePlan, organizationName } = useOrgPlan();
  const { data: licenseStats } = useEnterpriseLicenseStats(orgId);
  const [requestOpen, setRequestOpen] = useState(false);

  // ── Tab 1: Company Data ──
  const { data: org, isLoading: orgLoading } = useQuery({
    queryKey: ['org-settings', orgId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('organizations')
        .select('id, name, sector, country, contact_name, contact_email, contact_phone, employee_count')
        .eq('id', orgId!)
        .single();
      if (error) throw error;
      return data;
    },
    enabled: !!orgId,
  });

  const [companyData, setCompanyData] = useState({
    name: '', sector: '', employee_count: '', country: '',
    contact_name: '', contact_email: '',
  });

  useEffect(() => {
    if (org) {
      setCompanyData({
        name: org.name || '',
        sector: org.sector || '',
        employee_count: org.employee_count?.toString() || '',
        country: org.country || '',
        contact_name: org.contact_name || '',
        contact_email: org.contact_email || '',
      });
    }
  }, [org]);

  const saveCompanyMutation = useMutation({
    mutationFn: async () => {
      const { error } = await supabase
        .from('organizations')
        .update({
          name: companyData.name,
          sector: companyData.sector,
          country: companyData.country,
          contact_name: companyData.contact_name,
          contact_email: companyData.contact_email,
          employee_count: companyData.employee_count ? parseInt(companyData.employee_count) : null,
        })
        .eq('id', orgId!);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['org-settings', orgId] });
      toast({ title: 'Configuración guardada', description: 'Los datos de la empresa se han actualizado.' });
    },
    onError: () => {
      toast({ title: 'Error', description: 'No se pudieron guardar los cambios.', variant: 'destructive' });
    },
  });

  // ── Tab 2: Impact Levels ──
  const impactKey = orgId ? `impact_levels_${orgId}` : '';

  const { data: savedImpactLevels, isLoading: impactLoading } = useQuery({
    queryKey: ['platform-settings', impactKey],
    queryFn: async () => {
      const { data } = await supabase
        .from('platform_settings')
        .select('value')
        .eq('key', impactKey)
        .maybeSingle();
      return data?.value as typeof DEFAULT_IMPACT_LEVELS | null;
    },
    enabled: !!orgId,
  });

  const [impactLevels, setImpactLevels] = useState(DEFAULT_IMPACT_LEVELS);

  useEffect(() => {
    if (savedImpactLevels && Array.isArray(savedImpactLevels)) {
      setImpactLevels(savedImpactLevels);
    }
  }, [savedImpactLevels]);

  const saveImpactMutation = useMutation({
    mutationFn: async () => {
      const { error } = await supabase
        .from('platform_settings')
        .upsert({ key: impactKey, value: impactLevels as any }, { onConflict: 'key' });
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['platform-settings', impactKey] });
      toast({ title: 'Configuración guardada', description: 'Las tablas de impacto se han actualizado.' });
    },
    onError: () => {
      toast({ title: 'Error', description: 'No se pudieron guardar los cambios.', variant: 'destructive' });
    },
  });

  const updateImpactLevel = (index: number, field: 'level' | 'description', value: string) => {
    setImpactLevels(prev => prev.map((item, i) => i === index ? { ...item, [field]: value } : item));
  };

  // ── Tab 3: Evaluation Weights ──
  const weightsKey = orgId ? `evaluation_weights_${orgId}` : '';

  const { data: savedWeights, isLoading: weightsLoading } = useQuery({
    queryKey: ['platform-settings', weightsKey],
    queryFn: async () => {
      const { data } = await supabase
        .from('platform_settings')
        .select('value')
        .eq('key', weightsKey)
        .maybeSingle();
      return data?.value as Record<string, number> | null;
    },
    enabled: !!orgId,
  });

  const [weights, setWeights] = useState<Record<string, number>>(
    Object.fromEntries(LIDER_IA_COMPONENTS.map(c => [c.id, c.weight]))
  );

  useEffect(() => {
    if (savedWeights && typeof savedWeights === 'object') {
      setWeights(savedWeights);
    }
  }, [savedWeights]);

  const saveWeightsMutation = useMutation({
    mutationFn: async () => {
      const { error } = await supabase
        .from('platform_settings')
        .upsert({ key: weightsKey, value: weights as any }, { onConflict: 'key' });
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['platform-settings', weightsKey] });
      toast({ title: 'Configuración guardada', description: 'Las métricas de evaluación de proyectos se han actualizado.' });
    },
    onError: () => {
      toast({ title: 'Error', description: 'No se pudieron guardar los cambios.', variant: 'destructive' });
    },
  });

  const updateWeight = (key: string, value: number[]) => {
    setWeights(prev => ({ ...prev, [key]: value[0] }));
  };

  const totalWeight = Object.values(weights).reduce((a, b) => a + b, 0);
  const dimensions = ['L', 'I', 'D', 'E', 'R'];

  const isLoading = orgLoading || impactLoading || weightsLoading;

  if (!orgId) {
    return (
      <div className="p-8 text-center text-muted-foreground">
        No se encontró una organización asociada a tu cuenta.
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <h1 className="text-2xl font-display font-bold text-foreground">Configuración de la Empresa</h1>

      {/* Plan activo */}
      <Card className="glass-card">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Crown className="w-5 h-5 text-primary" />
            Plan de la empresa
          </CardTitle>
        </CardHeader>
        <CardContent className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          <div className="space-y-1">
            <div className="flex items-center gap-2">
              <span className="text-sm text-muted-foreground">Plan actual:</span>
              {isFreePlan ? (
                <Badge variant="outline">Gratuito</Badge>
              ) : (
                <Badge>Enterprise</Badge>
              )}
            </div>
            <p className="text-sm text-muted-foreground">
              Licencias Enterprise: <span className="font-medium text-foreground">{licenseStats?.used ?? 0}</span> en uso
              {' / '}
              <span className="font-medium text-foreground">{licenseStats?.max ?? 0}</span> contratadas
              {(licenseStats?.max ?? 0) > 0 && (
                <> · <span className="font-medium text-foreground">{Math.max((licenseStats?.max ?? 0) - (licenseStats?.used ?? 0), 0)}</span> disponibles</>
              )}
            </p>
            {licenseStats?.contractEndDate && (
              <p className="text-sm text-muted-foreground">
                Vigencia del contrato: <span className="font-medium text-foreground">{formatContractDate(licenseStats.contractStartDate)} – {formatContractDate(licenseStats.contractEndDate)}</span>
              </p>
            )}
            {licenseStats?.contract.isExpiringSoon && (
              <p className="text-sm font-medium text-warning">
                Tu contrato Enterprise vence en {licenseStats.contract.daysRemaining} días ({formatContractDate(licenseStats.contractEndDate)}). Solicita la renovación para no perder el acceso a los módulos avanzados.
              </p>
            )}
            {licenseStats?.contract.isExpired && (
              <p className="text-sm font-medium text-destructive">
                El contrato Enterprise venció el {formatContractDate(licenseStats.contractEndDate)}. La empresa opera en plan Gratuito; todo su historial se conserva.
              </p>
            )}
            {isFreePlan && (
              <p className="text-xs text-muted-foreground">
                Con el plan gratuito tu equipo accede como usuarios gratuitos. Contrata licencias Enterprise para habilitar los módulos de gestión y gobierno de IA.
              </p>
            )}
          </div>
          {(isFreePlan || licenseStats?.contract.isExpiringSoon || (licenseStats?.max ?? 0) === 0 || (licenseStats?.used ?? 0) >= (licenseStats?.max ?? 0)) && (
            <Button className="gap-2 shrink-0" onClick={() => setRequestOpen(true)}>
              <Sparkles className="w-4 h-4" />
              Contratar licencias Enterprise
            </Button>
          )}
        </CardContent>
      </Card>

      <Tabs defaultValue="company" className="space-y-6">
        <TabsList className="grid w-full grid-cols-4 h-12">
          <TabsTrigger value="company" className="flex items-center gap-2 text-sm">
            <Building2 className="w-4 h-4" />
            Datos de la Empresa
          </TabsTrigger>
          <TabsTrigger value="impact" className="flex items-center gap-2 text-sm">
            <Shield className="w-4 h-4" />
            Tablas de Impacto
          </TabsTrigger>
          <TabsTrigger value="weights" className="flex items-center gap-2 text-sm">
            <BarChart3 className="w-4 h-4" />
            Evaluación de Proyectos
          </TabsTrigger>
          <TabsTrigger value="build_buy" className="flex items-center gap-2 text-sm">
            <Scale className="w-4 h-4" />
            Protocolo Build/Buy
          </TabsTrigger>
        </TabsList>

        {/* Tab: Datos de la Empresa */}
        <TabsContent value="company">
          <Card className="glass-card">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Building2 className="w-5 h-5 text-primary" />
                Información de la Empresa
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {orgLoading ? (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {Array.from({ length: 6 }).map((_, i) => (
                    <div key={i} className="space-y-2">
                      <Skeleton className="h-4 w-32" />
                      <Skeleton className="h-10 w-full" />
                    </div>
                  ))}
                </div>
              ) : (
                <>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Nombre de la Empresa</Label>
                      <Input value={companyData.name} onChange={e => setCompanyData(prev => ({ ...prev, name: e.target.value }))} />
                    </div>
                    <div className="space-y-2">
                      <Label>Industria / Sector</Label>
                      <Input value={companyData.sector} onChange={e => setCompanyData(prev => ({ ...prev, sector: e.target.value }))} />
                    </div>
                    <div className="space-y-2">
                      <Label>Número de Empleados</Label>
                      <Input type="number" value={companyData.employee_count} onChange={e => setCompanyData(prev => ({ ...prev, employee_count: e.target.value }))} />
                    </div>
                    <div className="space-y-2">
                      <Label>País</Label>
                      <Input value={companyData.country} onChange={e => setCompanyData(prev => ({ ...prev, country: e.target.value }))} />
                    </div>
                    <div className="space-y-2">
                      <Label>Contacto Principal</Label>
                      <Input value={companyData.contact_name} onChange={e => setCompanyData(prev => ({ ...prev, contact_name: e.target.value }))} />
                    </div>
                    <div className="space-y-2">
                      <Label>Email de Contacto</Label>
                      <Input value={companyData.contact_email} onChange={e => setCompanyData(prev => ({ ...prev, contact_email: e.target.value }))} />
                    </div>
                  </div>
                  <div className="flex justify-end pt-4">
                    <Button onClick={() => saveCompanyMutation.mutate()} disabled={saveCompanyMutation.isPending} className="gap-2">
                      {saveCompanyMutation.isPending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                      Guardar Cambios
                    </Button>
                  </div>
                </>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Tab: Tablas de Impacto */}
        <TabsContent value="impact">
          <Card className="glass-card">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Shield className="w-5 h-5 text-primary" />
                Tablas de Impacto — Gestión de Riesgos
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-sm text-muted-foreground mb-4">
                Define los niveles de impacto que se utilizarán en la evaluación de riesgos de los proyectos de IA.
              </p>
              {impactLoading ? (
                <div className="space-y-3">
                  {Array.from({ length: 5 }).map((_, i) => <Skeleton key={i} className="h-20 w-full" />)}
                </div>
              ) : (
                <>
                  <div className="space-y-3">
                    {impactLevels.map((level, index) => (
                      <div key={level.id} className="flex items-start gap-4 p-4 bg-muted/50 rounded-xl">
                        <div className={`w-10 h-10 rounded-lg flex items-center justify-center font-bold text-white shrink-0 ${
                          level.value === 1 ? 'bg-green-500' :
                          level.value === 2 ? 'bg-lime-500' :
                          level.value === 3 ? 'bg-yellow-500' :
                          level.value === 4 ? 'bg-orange-500' :
                          'bg-red-500'
                        }`}>
                          {level.value}
                        </div>
                        <div className="flex-1 space-y-2">
                          <Input
                            value={level.level}
                            onChange={e => updateImpactLevel(index, 'level', e.target.value)}
                            className="font-semibold"
                            placeholder="Nombre del nivel"
                          />
                          <Input
                            value={level.description}
                            onChange={e => updateImpactLevel(index, 'description', e.target.value)}
                            className="text-sm"
                            placeholder="Descripción del nivel"
                          />
                        </div>
                      </div>
                    ))}
                  </div>
                  <div className="flex justify-end pt-4">
                    <Button onClick={() => saveImpactMutation.mutate()} disabled={saveImpactMutation.isPending} className="gap-2">
                      {saveImpactMutation.isPending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                      Guardar Cambios
                    </Button>
                  </div>
                </>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Tab: Evaluación de Proyectos */}
        <TabsContent value="weights">
          <Card className="glass-card">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <BarChart3 className="w-5 h-5 text-primary" />
                Evaluación de Proyectos — Marco LIDER-IA (12 Componentes)
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              <p className="text-sm text-muted-foreground">
                Define las métricas y su peso porcentual para evaluar los proyectos de IA. La puntuación final se calcula: (Calificación 0-5) × (Peso). El total debe sumar 100%.
              </p>

              {weightsLoading ? (
                <div className="space-y-4">
                  {Array.from({ length: 5 }).map((_, i) => <Skeleton key={i} className="h-24 w-full" />)}
                </div>
              ) : (
                <>
                  {dimensions.map(dim => {
                    const dimComponents = LIDER_IA_COMPONENTS.filter(c => c.dimensionLetter === dim);
                    return (
                      <div key={dim} className="space-y-4">
                        <div className="flex items-center gap-2">
                          <Badge className={`${DIMENSION_COLORS[dim]} border-0 font-bold`}>
                            {dim}
                          </Badge>
                          <h3 className="font-semibold text-foreground text-sm">{DIMENSION_NAMES[dim]}</h3>
                        </div>
                        {dimComponents.map(comp => (
                          <div key={comp.id} className="space-y-2 pl-4 border-l-2 border-muted ml-3">
                            <div className="flex justify-between">
                              <div>
                                <p className="font-medium text-foreground text-sm">{comp.name}</p>
                                <p className="text-xs text-muted-foreground">{comp.description}</p>
                              </div>
                              <span className="text-lg font-bold text-primary shrink-0 ml-4">{weights[comp.id]}%</span>
                            </div>
                            <Slider
                              value={[weights[comp.id]]}
                              onValueChange={(v) => updateWeight(comp.id, v)}
                              max={30}
                              step={1}
                              className="w-full"
                            />
                          </div>
                        ))}
                      </div>
                    );
                  })}

                  <div className={`p-3 rounded-lg text-center font-semibold ${totalWeight === 100 ? 'bg-green-500/10 text-green-600' : 'bg-red-500/10 text-red-600'}`}>
                    Total: {totalWeight}% {totalWeight !== 100 && '(debe sumar 100%)'}
                  </div>

                  <div className="flex justify-end">
                    <Button onClick={() => saveWeightsMutation.mutate()} disabled={totalWeight !== 100 || saveWeightsMutation.isPending} className="gap-2">
                      {saveWeightsMutation.isPending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                      Guardar Cambios
                    </Button>
                  </div>
                </>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Tab: Protocolo Build/Buy/Outsource */}
        <TabsContent value="build_buy">
          <BuildBuyProtocolTab orgId={orgId} />
        </TabsContent>
      </Tabs>

      <RequestEnterpriseLicensesDialog
        open={requestOpen}
        onOpenChange={setRequestOpen}
        orgName={organizationName ?? undefined}
        used={licenseStats?.used ?? 0}
        max={licenseStats?.max ?? 0}
      />
    </div>
  );
}
